'use client';

import { useEffect, useState } from 'react';

import type { Store } from '../utils';

type ValueFromStore<S extends Store> = ReturnType<S['get']>;
type UseSharedStoreReturnValue<S extends Store> = [ValueFromStore<S>, S['set']];

/**
 * Use a Store.
 *
 * ```tsx
 * import { myCountStore } from './../stores';
 *
 * const MyComponent = () => {
 *   const [count, setCount] = useStore(myCountStore);
 *
 *   return (
 *     <div>
 *       Value: { count }
 *       <br />
 *       <button onClick={() => setCount(prev => prev + 1)}>
 *         Increase Count
 *       </button>
 *       <br />
 *       <button onClick={() => setCount(0)}>
 *         Reset Count
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useStore = <S extends Store<any>>(
  store: S,
  initial?: ValueFromStore<S>,
): UseSharedStoreReturnValue<S> => {
  const [value, setValue] = useState<ValueFromStore<S>>(initial ?? store.get());

  useEffect(() => {
    return store.onChange(value => setValue(value));
  }, [store]);

  return [value, store.set];
};
